import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { FaWhatsapp, FaPhone, FaChevronLeft, FaChevronRight } from 'react-icons/fa';
import { openWhatsAppChat } from '../../util/whatsappUtils';
import { Box, Grid } from '@mui/material';
import EnhancedPropertyCard from '../EnhancedPropertyCard';
import NoPropertiesAnimation from '../common/NoPropertiesAnimation';
import { searchPropertiesByType, getPropertiesByType } from '../../services/propertyService';
import './property-section.css';
import InvertedImg from '../../assets/img/Inverted9.svg';
import PaginationControls from '../common/PaginationControls';
import CallIcon from "@mui/icons-material/Call";
import { submitEnquiryForm } from '../../services/enquiryService';

const PropertySection = ({
    title,
    description,
    filters = {},
    type,
    isSearchMode = false,
    initialPropertiesPerPage = 6 // Default to 6 properties per page, can be customized
}) => {
    const navigate = useNavigate();
    const [currentPage, setCurrentPage] = useState(1);
    const [propertiesPerPage, setPropertiesPerPage] = useState(initialPropertiesPerPage);
    const [properties, setProperties] = useState([]);
    const [loading, setLoading] = useState(true);
    const [totalProperties, setTotalProperties] = useState(0);
    const [hasSearched, setHasSearched] = useState(false); // Track if search has been performed
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        phone: '',
        description: ''
    });
    const [isMenuOpen, setMenuOpen] = useState(false);
    const [showCallConfirmation, setShowCallConfirmation] = useState(false);

    const toggleMenu = () => {
        setMenuOpen(!isMenuOpen);
    };

    // Use the configurable properties per page value (minimum 3)
    const PROPERTIES_PER_PAGE = Math.max(3, propertiesPerPage);

    // Handle items per page change
    const handleItemsPerPageChange = (newValue) => {
        setPropertiesPerPage(newValue);
        setCurrentPage(1); // Reset to first page when changing items per page
    };

    // Ensure current page is valid when properties or items per page change
    useEffect(() => {
        if (properties.length > 0) {
            const maxPages = Math.ceil(properties.length / PROPERTIES_PER_PAGE);
            if (currentPage > maxPages) {
                setCurrentPage(Math.max(1, maxPages));
            }
        }
    }, [properties.length, PROPERTIES_PER_PAGE, currentPage]);

    // Fetch properties from API
    useEffect(() => {
        // Don't call regular API if we've already performed a search
        if (hasSearched) {
            return;
        }

        const fetchProperties = async () => {
            try {
                setLoading(true);

                // Build search filters - Remove pagination parameters for client-side pagination
                const searchFilters = {};

                // If type is provided, use it as propertyType
                if (type) {
                    searchFilters.type = type;
                }

                console.log('Fetching properties with filters:', searchFilters);
                console.log('Search mode:', isSearchMode);

                let response;

                if (type && ['residential', 'commercial', 'agricultural'].includes(type.toLowerCase())) {
                    // Use regular API for initial load - get all properties
                    console.log('Using regular API for initial load');
                    response = await getPropertiesByType(type, searchFilters);
                } else {
                    // Fallback for unknown types
                    response = await getPropertiesByType(type || 'all', searchFilters);
                }

                if (Array.isArray(response)) {
                    setProperties(response);
                    setTotalProperties(response.length);
                } else {
                    setProperties([]);
                    setTotalProperties(0);
                }

            } catch (error) {
                console.error('Error fetching properties:', error);
                setProperties([]);
                setTotalProperties(0);
            } finally {
                setLoading(false);
            }
        };

        fetchProperties();
    }, [type, hasSearched]); // Removed currentPage from dependencies since we're doing client-side pagination

    // Separate effect to handle search when search mode is explicitly triggered
    useEffect(() => {
        if (isSearchMode) {
            const fetchSearchResults = async () => {
                try {
                    setLoading(true);

                    const searchParams = {};
                    if (filters.keyword) searchParams.keyword = filters.keyword;
                    if (filters.area) searchParams.area = filters.area;
                    if (filters.sqYards) searchParams.sqYards = filters.sqYards;
                    if (filters.acres) searchParams.acres = filters.acres;

                    console.log('Searching with params:', searchParams);

                    if (type && ['residential', 'commercial', 'agricultural'].includes(type.toLowerCase())) {
                        const response = await searchPropertiesByType(type, searchParams);

                        if (Array.isArray(response)) {
                            setProperties(response);
                            setTotalProperties(response.length);
                            setCurrentPage(1); // Reset to first page for search results
                            setHasSearched(true); // Mark that search has been performed
                        } else {
                            setProperties([]);
                            setTotalProperties(0);
                        }
                    }
                } catch (error) {
                    console.error('Error searching properties:', error);
                    setProperties([]);
                    setTotalProperties(0);
                } finally {
                    setLoading(false);
                }
            };

            fetchSearchResults();
        }
    }, [isSearchMode, type]); // Removed 'filters' from dependency array

    // Reset to first page when filters change
    useEffect(() => {
        setCurrentPage(1);
    }, [filters]);

    const handleChange = (e) => {
        const { name, value } = e.target;

        if (name === 'phone') {
            // Remove any non-numeric characters
            const onlyNums = value.replace(/[^0-9]/g, '');

            // Restriction 1: Max 10 digits
            if (onlyNums.length > 10) return;

            // Restriction 2: Indian mobile numbers usually start with 6, 7, 8, or 9
            if (onlyNums.length > 0 && !/^[6-9]/.test(onlyNums)) return;

            setFormData(prev => ({
                ...prev,
                [name]: onlyNums
            }));
        } else {
            setFormData(prev => ({
                ...prev,
                [name]: value
            }));
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            const payload = {
                ...formData,
                type: type || 'all'
            };
            console.log('Submitting enquiry:', payload);

            const response = await submitEnquiryForm(payload);

            if (response && (response._id || response.enquiry)) {
                alert('Thank you for your enquiry! We will contact you soon.');
                setFormData({
                    name: '',
                    email: '',
                    phone: '',
                    description: ''
                });
            } else {
                alert('Something went wrong. Please try again later.');
            }
        } catch (error) {
            console.error('Error submitting enquiry:', error);
            alert('Error sending message. Please check your connection.');
        }
    };

    // Pagination logic
    const totalPages = Math.ceil(totalProperties / PROPERTIES_PER_PAGE);
    const startIndex = (currentPage - 1) * PROPERTIES_PER_PAGE;
    const endIndex = Math.min(startIndex + PROPERTIES_PER_PAGE, totalProperties);

    // Get the properties to display for current page
    const currentPageProperties = properties.slice(startIndex, endIndex);

    const handlePageChange = (pageNumber) => {
        setCurrentPage(pageNumber);
        // Scroll to properties section
        document.querySelector('.available-properties-section')?.scrollIntoView({
            behavior: 'smooth'
        });
    };

    const handlePrevPage = () => {
        if (currentPage > 1) {
            handlePageChange(currentPage - 1);
        }
    };

    const handleNextPage = () => {
        if (currentPage < totalPages) {
            handlePageChange(currentPage + 1);
        }
    };

    const handleCallClick = () => {
        setShowCallConfirmation(true);
    };

    const handleConfirmCall = () => {
        // Make a direct WhatsApp call to the specified number
        const phoneNumber = '917893092211';
        const whatsappCallUrl = `https://wa.me/${phoneNumber}?call=1`;
        window.open(whatsappCallUrl, '_blank');
        setShowCallConfirmation(false);
    };

    const handleCancelCall = () => {
        setShowCallConfirmation(false);
    };

    return (
        <section className="property-section">
            <div className="container">
                <div className="property-intro">
                    <div className="intro-content">
                        <h2>{title}</h2>
                        <p>{description}</p>
                        <button className="enquire-btn" onClick={() => navigate('/contact')}>
                            ENQUIRE NOW →
                        </button>
                    </div>

                    <div className="contact-form-card">
                        <div className="royale-spaces-badge">
                            <Link to="/" onClick={toggleMenu}
                                href="/"
                                sx={{
                                    display: 'inline-block',
                                    textDecoration: 'none'
                                }}
                            >
                                <img
                                    src={InvertedImg}
                                    alt="Royale Spaces Logo"
                                    style={{
                                        // maxWidth: '150px',
                                        height: 'auto',
                                        width: '100%'
                                    }}
                                />
                            </Link>
                        </div>

                        <form onSubmit={handleSubmit} className="quick-contact-form">
                            <input
                                type="text"
                                name="name"
                                placeholder="Your Name"
                                value={formData.name}
                                onChange={handleChange}
                                required
                            />
                            <input
                                type="tel"
                                name="phone"
                                placeholder="Phone Number"
                                value={formData.phone}
                                onChange={handleChange}
                                required
                            />
                            <input
                                type="email"
                                name="email"
                                placeholder="Email Address"
                                value={formData.email}
                                onChange={handleChange}
                                required
                            />
                            <textarea
                                name="description"
                                className="enquiry-textarea"
                                placeholder="How can we help you? (e.g., Interested in 250 Sq.Yards plot)"
                                value={formData.description}
                                onChange={handleChange}
                                required
                            />
                            <div className="form-checkbox">
                                <input type="checkbox" id="terms" required />
                                <label htmlFor="terms">
                                    By submitting this form I agree to Terms of Use
                                </label>
                            </div>
                            <div className="form-actions">
                                <button type="submit" className="send-message-btn">
                                    Send Message
                                </button>
                                <button type="button" className="call-btn" onClick={handleCallClick}>
                                    <CallIcon sx={{ mr: 0.5 }} /> Call
                                </button>
                            </div>
                            <button
                                type="button"
                                className="whatsapp-btn"
                                onClick={() => openWhatsAppChat()}
                            >
                                <FaWhatsapp /> WhatsApp
                            </button>
                        </form>
                    </div>
                </div>

                <div className="available-properties-section">
                    <div className="properties-header">
                        <h2>Available Properties</h2>
                        <p className="properties-count">
                            {loading ? 'Loading...' : `Showing ${startIndex + 1}-${Math.min(endIndex, totalProperties)} of ${totalProperties} properties`}
                        </p>
                    </div>

                    {/* Loading State */}
                    {loading && (
                        <Box
                            sx={{
                                display: 'flex',
                                justifyContent: 'center',
                                alignItems: 'center',
                                minHeight: '400px',
                                color: 'white'
                            }}
                        >
                            <div style={{ textAlign: 'center' }}>
                                <div style={{
                                    border: '4px solid #f3f3f3',
                                    borderTop: '4px solid #d8b035',
                                    borderRadius: '50%',
                                    width: '50px',
                                    height: '50px',
                                    animation: 'spin 1s linear infinite',
                                    margin: '0 auto 20px'
                                }}></div>
                                <p>Loading properties...</p>
                            </div>
                        </Box>
                    )}

                    {/* Properties Grid - Using MUI Grid for uniform sizing */}
                    {!loading && (
                        <Box sx={{ width: '100%', mt: 4, px: 2 }}>
                            <Grid
                                container
                                spacing={3}
                                sx={{
                                    maxWidth: '1300px',
                                    margin: '0 auto',
                                    width: '100%'
                                }}
                            >
                                {currentPageProperties.map((property, index) => (
                                    <Grid
                                        item
                                        xs={12}
                                        sm={6}
                                        md={4}
                                        key={property._id || property.id || index}
                                        sx={{ display: 'flex', justifyContent: 'center' }} // Centering card within grid column
                                    >
                                        <EnhancedPropertyCard property={property} propertyType={type} />
                                    </Grid>
                                ))}
                            </Grid>

                            {/* Show animation if no properties found */}
                            {properties.length === 0 && (
                                <NoPropertiesAnimation type={type} />
                            )}
                        </Box>
                    )}

                    {/* Pagination - Show if there are properties */}
                    {!loading && properties.length > 0 && (
                        <PaginationControls
                            currentPage={currentPage}
                            totalPages={totalPages}
                            totalItems={totalProperties}
                            itemsPerPage={PROPERTIES_PER_PAGE}
                            onPageChange={handlePageChange}
                            onItemsPerPageChange={handleItemsPerPageChange}
                            itemsPerPageOptions={[3, 6, 9, 12, 18, 24]}
                            showItemsPerPage={true}
                            maxPageButtons={5}
                        />
                    )}
                </div>
            </div>

            {/* Call Confirmation Dialog */}
            {showCallConfirmation && (
                <div className="call-confirmation-overlay">
                    <div className="call-confirmation-dialog">
                        <div className="dialog-header">
                            <h3>Confirm Call Request</h3>
                        </div>
                        <div className="dialog-content">
                            <p>Would you like to make a direct call to <strong>+91-7893092211</strong> via WhatsApp?</p>
                            <p>This will open WhatsApp and initiate a call to our team.</p>
                        </div>
                        <div className="dialog-actions">
                            <button
                                className="cancel-btn"
                                onClick={handleCancelCall}
                            >
                                Cancel
                            </button>
                            <button
                                className="confirm-btn"
                                onClick={handleConfirmCall}
                            >
                                <FaWhatsapp /> Yes, Call Now
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </section>
    );
};

export default PropertySection;